import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft, Bell, BookOpen, CalendarDays, Cast, Check, ChevronLeft, Clock3,
  Download, Film, Flag, Heart, Home, Library, ListFilter, LogIn, Menu,
  MessageCircle, Moon, Newspaper, Pencil, Play, Search, Send, Settings, Star,
  Sun, ThumbsDown, ThumbsUp, Trash2, UserRound, X
} from 'lucide-react';
import {
  Link, NavLink, Navigate, Route, Routes, useLocation, useNavigate, useParams,
  useSearchParams
} from 'react-router-dom';
import { animeApi } from './api';
import { saveUser, useFavorites, useHistory, useSeenEpisodes, useUser, useWatchedAnime, useWatching, useWatchLater } from './store';
import type { Anime, AnimeInfo, Comment, Episode, HomePayload, NativeBackupState, NewsItem, User } from './types';

const placeholder = '/assets/placeholder.png';
const dayNames: Record<string, string> = {
  '1': 'الأحد', '2': 'الإثنين', '3': 'الثلاثاء', '4': 'الأربعاء',
  '5': 'الخميس', '6': 'الجمعة', '7': 'السبت'
};

const SERVER_SETTINGS_KEY = 'anime-cloud:server-settings';
const SAFE_BACKUP_KEYS = [
  'anime-cloud:favorites', 'anime-cloud:history', 'anime-cloud:seen-episodes',
  'anime-cloud:watched-anime', 'anime-cloud:watch-later', 'anime-cloud:watching',
  'anime-cloud:seen-map',
  'anime-cloud:theme', 'anime-cloud:comments', 'anime-cloud:seen',
  'anime-cloud:children-mode', 'anime-cloud:children-pin', 'anime-cloud:hidden-mode', 'anime-cloud:reported-comments'
] as const;
const ARRAY_BACKUP_KEYS = new Set<string>(['anime-cloud:favorites', 'anime-cloud:watched-anime', 'anime-cloud:watch-later', 'anime-cloud:watching', 'anime-cloud:history', 'anime-cloud:seen-episodes', 'anime-cloud:reported-comments']);

function readStoredIds(key: string) {
  try {
    const value: unknown = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  } catch { return []; }
}

function validateBackupValue(key: string, value: unknown) {
  if (typeof value !== 'string') throw new Error(`قيمة غير صالحة: ${key}`);
  if (ARRAY_BACKUP_KEYS.has(key)) {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed) || parsed.length > 10_000 || parsed.some((item) => typeof item !== 'string' || item.length > 200)) throw new Error(`قائمة غير صالحة: ${key}`);
    return JSON.stringify(parsed);
  }
  if (key === 'anime-cloud:seen-map') {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed) || Object.entries(parsed).some(([episodeId, animeId]) => episodeId.length > 200 || typeof animeId !== 'string' || animeId.length > 200)) throw new Error('خريطة الحلقات المشاهدة غير صالحة');
    return JSON.stringify(parsed);
  }
  if (key === 'anime-cloud:children-pin') {
    if (!/^[a-f0-9]{64}$/.test(value)) throw new Error('رمز وضع الأطفال غير صالح');
    return value;
  }
  if (key === 'anime-cloud:theme' && !['dark', 'light'].includes(value)) throw new Error('وضع المظهر غير صالح');
  if (key !== 'anime-cloud:theme' && !['true', 'false'].includes(value)) throw new Error(`إعداد غير صالح: ${key}`);
  return value;
}

function applyNativeBackupState(state: NativeBackupState) {
  const entries = [
    ['anime-cloud:favorites', JSON.stringify(state.favorites)],
    ['anime-cloud:watched-anime', JSON.stringify(state.favoritesByType?.['1'] || [])],
    ['anime-cloud:watch-later', JSON.stringify(state.favoritesByType?.['2'] || [])],
    ['anime-cloud:watching', JSON.stringify(state.favoritesByType?.['3'] || [])],
    ['anime-cloud:seen-episodes', JSON.stringify(state.seenEpisodes)],
    ['anime-cloud:seen-map', JSON.stringify(state.seenMap)],
    ['anime-cloud:history', JSON.stringify(state.history)],
    ['anime-cloud:reported-comments', JSON.stringify(state.reportedComments)]
  ] as const;
  const validated = entries.map(([key, value]) => [key, validateBackupValue(key, value)] as const);
  validated.forEach(([key, value]) => localStorage.setItem(key, value));
}

async function profileJpeg(file: File) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new window.Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('تعذر قراءة الصورة'));
      element.src = objectUrl;
    });
    const scale = Math.min(1, 1024 / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('تعذر تجهيز الصورة');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new Error('تعذر تحويل الصورة')), 'image/jpeg', 0.9));
    return new File([blob], 'temp.jpeg', { type: 'image/jpeg' });
  } finally { URL.revokeObjectURL(objectUrl); }
}

function saveServerSettings(settings?: Record<string, unknown>) {
  if (!settings || !Object.keys(settings).length) return;
  localStorage.setItem(SERVER_SETTINGS_KEY, JSON.stringify(settings));
}

function readServerSettings(): Record<string, unknown> {
  try { return JSON.parse(localStorage.getItem(SERVER_SETTINGS_KEY) || '{}'); }
  catch { return {}; }
}

function readAnnouncement(value: unknown) {
  if (typeof value === 'string') return value.trim();
  if (!value || typeof value !== 'object') return '';
  const message = value as Record<string, unknown>;
  return [message.title, message.message].filter((part) => typeof part === 'string' && part.trim()).join(' — ');
}

function extractRefreshUrl(html: string) {
  if (!html) return '';
  const document = new DOMParser().parseFromString(html, 'text/html');
  const content = document.querySelector('meta[http-equiv="refresh" i]')?.getAttribute('content') || '';
  const url = content.match(/url\s*=\s*['"]?([^'";\s]+)/i)?.[1] || '';
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol) ? parsed.toString() : '';
  } catch { return ''; }
}

function trackOnce(key: string, request: () => Promise<unknown>) {
  if (sessionStorage.getItem(key)) return;
  sessionStorage.setItem(key, '1');
  void request().catch(() => sessionStorage.removeItem(key));
}

function requireMutationSuccess(response: unknown) {
  if (!response || typeof response !== 'object') throw new Error('الخادم لم يؤكد العملية');
  const root = response as Record<string, unknown>;
  const rawResult = root.result;
  const first = Array.isArray(rawResult) ? rawResult[0] : rawResult;
  const record = first && typeof first === 'object' ? first as Record<string, unknown> : undefined;
  const rawStatus = record?.status ?? root.status ?? (typeof first === 'string' ? first : undefined);
  if (rawStatus !== undefined) {
    const status = String(rawStatus);
    if (!['Done', 'true', '1', 'Success', 'success'].includes(status)) throw new Error(String(record?.message || record?.title || status || 'رفض الخادم العملية'));
    return;
  }
  if (!record || Object.keys(record).length === 0) throw new Error('الخادم لم يؤكد العملية');
}

function animeHref(anime: Anime) {
  const query = new URLSearchParams({ name: anime.name || '', image: anime.image || '', status: anime.status || '', year: anime.year || '' });
  return `/anime/${anime.id}?${query}`;
}

function useDocumentTitle(title: string) {
  useEffect(() => { document.title = title ? `${title} — Anime Cloud` : 'Anime Cloud'; }, [title]);
}

function Image({ src, alt, className = '' }: { src?: string; alt: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  return <img className={className} src={!src || failed ? placeholder : src} alt={alt} loading="lazy" onError={() => setFailed(true)} />;
}

function Spinner({ label = 'جاري التحميل' }: { label?: string }) {
  return <div className="loading"><span className="spinner" /><span>{label}</span></div>;
}

function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return <div className="empty-state"><span className="empty-icon">!</span><h3>تعذر تحميل المحتوى</h3><p>{message}</p>{retry && <button className="button" onClick={retry}>إعادة المحاولة</button>}</div>;
}

function EmptyState({ icon = <Film />, title, body }: { icon?: ReactNode; title: string; body: string }) {
  return <div className="empty-state"><span className="empty-icon">{icon}</span><h3>{title}</h3><p>{body}</p></div>;
}

function AppShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const user = useUser();
  const location = useLocation();
  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    if (!user) return;
    void animeApi.session().then((serverUser) => saveUser({ ...user, ...serverUser })).catch(() => saveUser(null));
  }, [user?.userid]);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (query.trim()) navigate(`/catalog?q=${encodeURIComponent(query.trim())}`);
  };
  const nav = [
    { to: '/', label: 'الرئيسية', icon: <Home /> },
    { to: '/catalog', label: 'كل الأنمي', icon: <Film /> },
    { to: '/schedule', label: 'جدول العرض', icon: <CalendarDays /> },
    { to: '/news', label: 'الأخبار', icon: <Newspaper /> },
    { to: '/library', label: 'مكتبتي', icon: <Library /> },
    { to: '/settings', label: 'الإعدادات', icon: <Settings /> }
  ];
  return <div className="app-shell">
    <aside className={`sidebar ${menuOpen ? 'is-open' : ''}`}>
      <div className="brand"><img src="/assets/app-icon.png" alt="Anime Cloud" /><div><strong>ANIME CLOUD</strong><span>مشاهدتك تبدأ هنا</span></div></div>
      <nav>{nav.map((item) => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({ isActive }) => isActive ? 'active' : ''}>{item.icon}<span>{item.label}</span></NavLink>)}</nav>
      <div className="sidebar-account">
        <UserRound />
        <div><strong>{user?.username || 'حسابي'}</strong><span>{user?.email || 'سجّل لحفظ تفاعلاتك'}</span></div>
        <Link to={user ? '/settings' : '/auth'} aria-label="الحساب"><ChevronLeft /></Link>
      </div>
    </aside>
    {menuOpen && <button className="scrim" aria-label="إغلاق القائمة" onClick={() => setMenuOpen(false)} />}
    <main className="main-area">
      <header className="topbar">
        <button className="menu-button" onClick={() => setMenuOpen(true)} aria-label="فتح القائمة"><Menu /></button>
        <form className="global-search" onSubmit={submit}><Search /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="ابحث عن أنمي أو فيلم…" /><kbd>⌘ K</kbd></form>
        <div className="top-actions"><Link to="/news" className="icon-button" aria-label="الأخبار"><Bell /></Link><Link to={user ? '/settings' : '/auth'} className="avatar">{user?.profilePicture ? <Image src={user.profilePicture} alt={user.username} /> : (user?.username?.[0] || <UserRound />)}</Link></div>
      </header>
      <div className="content">{children}</div>
    </main>
    <nav className="bottom-nav">{nav.slice(0, 5).map((item) => <NavLink key={item.to} to={item.to} end={item.to === '/'}>{item.icon}<span>{item.label}</span></NavLink>)}</nav>
  </div>;
}

function AnimeCard({ anime, wide = false }: { anime: Anime; wide?: boolean }) {
  return <Link className={`anime-card ${wide ? 'wide' : ''}`} to={animeHref(anime)}>
    <div className="poster-wrap"><Image src={anime.image} alt={anime.name} /><span className={`status ${anime.status === 'مستمر' ? 'ongoing' : ''}`}>{anime.epName || anime.status || anime.year}</span><span className="card-play"><Play fill="currentColor" /></span></div>
    <div className="card-copy"><h3 title={anime.name}>{anime.name}</h3><p>{anime.year || '—'} {anime.status ? `• ${anime.status}` : ''}</p></div>
  </Link>;
}

function Section({ title, eyebrow, items, more }: { title: string; eyebrow?: string; items: Anime[]; more?: string }) {
  return <section className="rail-section"><div className="section-heading"><div>{eyebrow && <span>{eyebrow}</span>}<h2>{title}</h2></div>{more && <Link to={more}>عرض الكل <ArrowLeft /></Link>}</div><div className="card-rail">{items.map((anime) => <AnimeCard anime={anime} key={`${title}-${anime.id}`} />)}</div></section>;
}

function HomePage() {
  const [data, setData] = useState<HomePayload | null>(null);
  const [error, setError] = useState('');
  const load = () => {
    setError('');
    animeApi.home().then((payload) => {
      saveServerSettings(payload.SettingsResult);
      setData(payload);
    }).catch((e) => setError(e.message));
  };
  useEffect(load, []);
  useEffect(() => trackOnce('anime-cloud:visit:app', animeApi.trackAppVisit), []);
  useDocumentTitle('الرئيسية');
  if (error) return <ErrorState message={error} retry={load} />;
  if (!data) return <Spinner label="نبني السحابة…" />;
  const hero = data.result2?.[0] || data.result?.[0];
  const announcement = readAnnouncement(data.SettingsResult?.message);
  return <>
    {announcement && <div className="server-announcement"><Bell /> <span>{announcement}</span></div>}
    {hero && <section className="hero" style={{ '--hero-image': `url("${hero.image}")` } as React.CSSProperties}>
      <div className="hero-copy"><span className="hero-kicker"><span /> أحدث حلقة</span><h1>{hero.name}</h1><p>{hero.epName || 'اكتشف أحدث الإضافات على Anime Cloud'}</p><div className="hero-actions"><Link className="button primary" to={animeHref(hero)}><Play fill="currentColor" /> ابدأ المشاهدة</Link><Link className="button ghost" to="/catalog">تصفّح المكتبة</Link></div></div>
      <div className="hero-index">AC<span>/26</span></div>
    </section>}
    <Section eyebrow="الآن" title="أحدث الحلقات" items={(data.result2 || []).slice(0, 14)} more="/catalog?sort=latest" />
    <Section eyebrow="جديد" title="وصل حديثاً" items={(data.result || []).slice(0, 14)} more="/catalog" />
    <Section eyebrow="اختيارات" title="الأكثر مشاهدة" items={(data.result4 || []).slice(0, 14)} more="/catalog?filter=popular" />
    <Section eyebrow="أكمل" title="مقترح لك" items={(data.result3 || []).slice(0, 14)} more="/catalog?filter=rank" />
  </>;
}

const catalogFilters = [
  ['getAllAnime', 'الكل'], ['getMost', 'الأكثر مشاهدة'], ['getByRank', 'الأعلى تقييماً'],
  ['getByContinue', 'مستمر'], ['getByFinished', 'مكتمل'], ['getAllMovies', 'أفلام']
] as const;

function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const latest = params.get('sort') === 'latest';
  const command = params.get('command') || (params.get('filter') === 'rank' ? 'getByRank' : params.get('filter') === 'popular' ? 'getMost' : 'getAllAnime');
  const query = params.get('q') || '';
  const facet = params.get('name') || '';
  const [items, setItems] = useState<Anime[]>([]);
  const [facets, setFacets] = useState<{ genres: string[]; years: string[]; seasons: string[]; ages: string[] }>({ genres: [], years: [], seasons: [], ages: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState(60);
  useDocumentTitle(query ? `بحث: ${query}` : 'كل الأنمي');
  useEffect(() => {
    Promise.all([
      animeApi.facets('getGenres'), animeApi.facets('getYears'),
      animeApi.facets('getSeason'), animeApi.facets('getAges')
    ]).then(([genres, years, seasons, ages]) => setFacets({
      genres: genres.map((x) => x.name).filter(Boolean),
      years: years.map((x) => x.year).filter(Boolean),
      seasons: seasons.map((x) => x.name).filter(Boolean),
      ages: ages.map((x) => x.age).filter(Boolean)
    })).catch(() => undefined);
  }, []);
  useEffect(() => {
    setLoading(true); setError(''); setLimit(60);
    const request = latest ? animeApi.home().then((data) => data.result2 || []) : animeApi.catalog(command, facet ? { name: facet } : {});
    request.then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [command, facet, latest]);
  const filtered = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return term ? items.filter((x) => `${x.name} ${x.keywords || ''}`.toLocaleLowerCase().includes(term)) : items;
  }, [items, query]);
  return <section>
    <div className="page-heading"><div><span>{latest ? 'آخر الإضافات' : 'المكتبة الكاملة'}</span><h1>{query ? `نتائج “${query}”` : latest ? 'أحدث الحلقات' : 'كل الأنمي'}</h1><p>{loading ? '…' : `${filtered.length.toLocaleString('ar')} عنوان متاح`}</p></div><ListFilter /></div>
    <div className="filter-bar">{catalogFilters.map(([value, label]) => <button className={!latest && command === value ? 'active' : ''} onClick={() => setParams({ command: value, ...(query ? { q: query } : {}) })} key={value}>{label}</button>)}</div>
    <div className="facet-bar">
      <FacetSelect label="التصنيف" value={command === 'getByGenres' ? facet : ''} items={facets.genres} onChange={(name) => setParams(name ? { command: 'getByGenres', name } : {})} />
      <FacetSelect label="السنة" value={command === 'getByYear' ? facet : ''} items={facets.years} onChange={(name) => setParams(name ? { command: 'getByYear', name } : {})} />
      <FacetSelect label="الموسم" value={command === 'getBySeason' ? facet : ''} items={facets.seasons} onChange={(name) => setParams(name ? { command: 'getBySeason', name } : {})} />
      <FacetSelect label="العمر" value={command === 'getByAge' ? facet : ''} items={facets.ages} onChange={(name) => setParams(name ? { command: 'getByAge', name } : {})} />
    </div>
    {loading ? <Spinner /> : error ? <ErrorState message={error} /> : filtered.length ? <><div className="catalog-grid">{filtered.slice(0, limit).map((anime) => <AnimeCard anime={anime} key={anime.id} />)}</div>{limit < filtered.length && <button className="load-more" onClick={() => setLimit((x) => x + 60)}>عرض المزيد <span>{Math.min(60, filtered.length - limit)}</span></button>}</> : <EmptyState icon={<Search />} title="لا توجد نتائج" body="جرّب اسماً آخر أو غيّر التصنيف." />}
  </section>;
}

function FacetSelect({ label, value, items, onChange }: { label: string; value: string; items: string[]; onChange: (value: string) => void }) {
  return <label><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}><option value="">الكل</option>{items.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>;
}

function AnimeDetailsPage() {
  const { animeId = '' } = useParams();
  const [query] = useSearchParams();
  const queryName = query.get('name') || '';
  const [resolvedBase, setResolvedBase] = useState<Anime | null>(null);
  const base: Anime = resolvedBase?.id === animeId ? resolvedBase : { id: animeId, name: queryName || 'تفاصيل الأنمي', image: query.get('image') || '', status: query.get('status') || '', year: query.get('year') || '' };
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [info, setInfo] = useState<AnimeInfo>({});
  const [related, setRelated] = useState<Anime[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [episodeQuery, setEpisodeQuery] = useState('');
  const favorites = useFavorites();
  const watchedAnime = useWatchedAnime();
  const watchLater = useWatchLater();
  const watching = useWatching();
  const seenEpisodes = useSeenEpisodes();
  const libraryStores = [favorites, watchedAnime, watchLater, watching];
  const libraryType = libraryStores.findIndex((store) => store.has(animeId));
  const setLibraryType = (next: string) => {
    libraryStores.forEach((store) => { if (store.has(animeId)) store.toggle(animeId); });
    const index = Number(next);
    if (next !== '' && index >= 0 && index < libraryStores.length && !libraryStores[index].has(animeId)) libraryStores[index].toggle(animeId);
  };
  useDocumentTitle(base.name);
  useEffect(() => {
    setResolvedBase(null);
    if (queryName) return;
    void animeApi.catalog().then((items) => setResolvedBase(items.find((item) => item.id === animeId) || null)).catch(() => undefined);
  }, [animeId, queryName]);
  useEffect(() => {
    setLoading(true);
    Promise.all([animeApi.animeDetails(animeId), animeApi.animeMore(animeId)])
      .then(async ([details, more]) => {
        saveServerSettings(details.SettingsResult);
        setEpisodes(details.result || []);
        const combined = { ...(details.mainResult?.[0] || {}), ...more };
        setInfo(combined);
        const relatedRoot = combined.relatedID && combined.relatedID !== '0' ? combined.relatedID : '';
        setRelated(relatedRoot ? await animeApi.related(animeId, relatedRoot).catch(() => []) : []);
      }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [animeId]);
  useEffect(() => trackOnce(`anime-cloud:visit:anime:${animeId}`, () => animeApi.trackAnimeVisit(animeId)), [animeId]);
  const visibleEpisodes = episodes.filter((ep) => ep.name.includes(episodeQuery));
  const seenForAnime = Object.entries(seenEpisodes.animeByEpisode).filter(([, ownerAnimeId]) => ownerAnimeId === animeId).map(([episodeId]) => episodeId);
  return <>
    <section className="detail-hero" style={{ '--hero-image': `url("${base.image}")` } as React.CSSProperties}>
      <Link className="back-link" to="/"><ArrowLeft /> رجوع</Link>
      <div className="detail-poster"><Image src={base.image} alt={base.name} /></div>
      <div className="detail-copy"><span className="hero-kicker"><span /> {base.status || 'Anime Cloud'}</span><h1>{base.name}</h1><div className="meta-row"><span><Star fill="currentColor" /> {info.rank || '—'}</span><span>{base.year || '—'}</span><span>{info.age || 'كل الأعمار'}</span></div><p>{info.story || 'تفاصيل القصة تُحمّل من مكتبة Anime Cloud.'}</p><div className="genre-list">{(info.genres || '').split(',').filter(Boolean).map((genre) => <span key={genre}>{genre.trim()}</span>)}</div><div className="hero-actions"><button className={`button ${libraryType === 0 ? 'saved' : 'primary'}`} onClick={() => setLibraryType(libraryType === 0 ? '' : '0')}><Heart fill={libraryType === 0 ? 'currentColor' : 'none'} /> {libraryType === 0 ? 'في المفضلة' : 'أضف للمفضلة'}</button><label className="library-picker"><Library /><select aria-label="قائمة المكتبة" value={libraryType < 0 ? '' : String(libraryType)} onChange={(event) => setLibraryType(event.target.value)}><option value="">غير محفوظ</option><option value="0">المفضلة</option><option value="1">تمت مشاهدته</option><option value="2">أشاهده لاحقاً</option><option value="3">أشاهده حالياً</option></select></label></div></div>
    </section>
    <section className="episodes-section"><div className="section-heading"><div><span>قائمة العرض</span><h2>الحلقات <small>{episodes.length.toLocaleString('ar')}</small></h2></div><div className="episode-tools">{seenForAnime.length > 0 && <button className="link-button" onClick={() => seenForAnime.forEach((episodeId) => seenEpisodes.toggle(episodeId, animeId))}>مسح المشاهدة</button>}<label className="inline-search"><Search /><input value={episodeQuery} onChange={(e) => setEpisodeQuery(e.target.value)} placeholder="ابحث برقم الحلقة" /></label></div></div>
      {loading ? <Spinner /> : error ? <ErrorState message={error} /> : <div className="episode-list">{visibleEpisodes.map((episode, index) => <EpisodeRow key={episode.id} episode={episode} anime={base} index={index} seen={seenEpisodes.has(episode.id)} onToggle={() => seenEpisodes.toggle(episode.id, animeId)} />)}</div>}
    </section>
    {related.length > 0 && <Section eyebrow="قد يعجبك" title="أعمال مرتبطة" items={related} />}
    {localStorage.getItem('anime-cloud:comments') !== 'false' && <CommentsPanel episodeId={animeId} animeId={animeId} episodeName="" animeName={base.name} animeLevel />}
  </>;
}

function EpisodeRow({ episode, anime, index, seen, onToggle }: { episode: Episode; anime: Anime; index: number; seen: boolean; onToggle: () => void }) {
  const query = new URLSearchParams({ animeId: anime.id, animeName: anime.name, episodeName: episode.name, image: episode.image300 || episode.image170 || anime.image, comments: episode.enableComment || '1' });
  return <div className={`episode-row ${seen ? 'seen' : ''}`}><button className="episode-number" onClick={onToggle} aria-label={seen ? `إلغاء مشاهدة ${episode.name}` : `تمييز ${episode.name} كمشاهدة`}>{seen ? <Check /> : String(index + 1).padStart(2, '0')}</button><Link className="episode-link" to={`/watch/${episode.id}?${query}`}><Image src={episode.image170 || anime.image} alt={episode.name} /><div><h3>{episode.name}</h3><p>{anime.name} {episode.filer ? `• ${episode.filer}` : ''}</p></div><span className="episode-play"><Play fill="currentColor" /></span></Link></div>;
}

function WatchPage() {
  const { episodeId = '' } = useParams();
  const [query] = useSearchParams();
  const animeId = query.get('animeId') || '';
  const animeName = query.get('animeName') || 'Anime Cloud';
  const episodeName = query.get('episodeName') || 'مشاهدة الحلقة';
  const image = query.get('image') || '';
  const [quality, setQuality] = useState<'hd' | 'sd'>('hd');
  const [videoUrl, setVideoUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [playerNotice, setPlayerNotice] = useState('');
  const history = useHistory();
  const seenEpisodes = useSeenEpisodes();
  const markedEpisode = useRef('');
  const commentsEnabled = localStorage.getItem('anime-cloud:comments') !== 'false' && query.get('comments') !== '0';
  useDocumentTitle(`${animeName} — ${episodeName}`);
  const markViewed = () => {
    if (markedEpisode.current === episodeId || localStorage.getItem('anime-cloud:seen') === 'false') return;
    markedEpisode.current = episodeId;
    if (animeId) history.add(animeId);
    seenEpisodes.add(episodeId, animeId);
  };
  const resolveVideo = async (nextQuality = quality) => {
    setLoading(true); setError('');
    try { setVideoUrl((await animeApi.video(episodeId, nextQuality)).url); }
    catch (e) { setError(e instanceof Error ? e.message : 'الفيديو غير متاح'); }
    finally { setLoading(false); }
  };
  useEffect(() => { void resolveVideo(); }, [episodeId]);
  const changeQuality = (next: 'hd' | 'sd') => { setQuality(next); resolveVideo(next); };
  const castVideo = async () => {
    const video = document.querySelector('.player-shell video') as (HTMLVideoElement & { remote?: { prompt: () => Promise<void> } }) | null;
    setPlayerNotice('');
    if (!video?.remote) return setPlayerNotice('البث الخارجي غير مدعوم في هذا المتصفح.');
    try { await video.remote.prompt(); }
    catch { setPlayerNotice('لم يتم اختيار جهاز بث.'); }
  };
  return <section className="watch-page">
    <div className="watch-heading"><Link to={animeId ? `/anime/${animeId}?name=${encodeURIComponent(animeName)}&image=${encodeURIComponent(image)}` : '/'}><ArrowLeft /> العودة للحلقات</Link><div><span>{animeName}</span><h1>{episodeName}</h1></div></div>
    <div className="player-shell">
      {videoUrl ? <video key={videoUrl} controls autoPlay playsInline poster={image} src={videoUrl} onPlay={markViewed} /> : <div className="player-placeholder" style={{ backgroundImage: `linear-gradient(#050913aa,#050913dd),url("${image}")` }}>{loading ? <Spinner label="نجهّز المشاهدة…" /> : <><Play /><p>{error || 'الفيديو غير متاح حالياً'}</p><button className="button" onClick={() => resolveVideo()}>إعادة المحاولة</button></>}</div>}
      <div className="player-toolbar"><div className="quality-switch"><button className={quality === 'hd' ? 'active' : ''} onClick={() => changeQuality('hd')}>HD</button><button className={quality === 'sd' ? 'active' : ''} onClick={() => changeQuality('sd')}>SD</button></div><div className="player-actions">{videoUrl && <button className="button ghost" onClick={() => void castVideo()}><Cast /> بث</button>}<a className="button ghost" href={`/api/download/${encodeURIComponent(episodeId)}?quality=${quality}`} download onClick={markViewed}><Download /> تنزيل</a></div></div>
      {playerNotice && <p className="player-notice">{playerNotice}</p>}
    </div>
    {commentsEnabled && <CommentsPanel episodeId={episodeId} animeId={animeId} episodeName={episodeName} animeName={animeName} />}
  </section>;
}

function CommentsPanel({ episodeId, animeId, episodeName, animeName, animeLevel = false }: { episodeId: string; animeId: string; episodeName: string; animeName: string; animeLevel?: boolean }) {
  const user = useUser();
  const [comments, setComments] = useState<Comment[]>([]);
  const [text, setText] = useState('');
  const [message, setMessage] = useState('');
  const [commentsError, setCommentsError] = useState('');
  const [orderBy, setOrderBy] = useState('3');
  const [hasMore, setHasMore] = useState(false);
  const [rules, setRules] = useState<{ title: string; message: string } | undefined>();
  const load = async (offset = 0, append = false) => {
    setCommentsError('');
    try {
      let next: Comment[] | null = null;
      let lastError: unknown;
      for (let attempt = 0; attempt < 2 && !next; attempt += 1) {
        try { next = animeLevel ? await animeApi.animeComments(animeId, offset, orderBy) : await animeApi.comments(episodeId, offset, orderBy); }
        catch (error) {
          lastError = error;
          if (attempt === 0) await new Promise((resolve) => window.setTimeout(resolve, 500));
        }
      }
      if (!next) throw lastError;
      setComments((current) => append ? [...current, ...next.filter((item) => !current.some((old) => (old.id || old.commentID) === (item.id || item.commentID)))] : next);
      setHasMore(next.length >= 20);
    } catch (error) {
      if (!append) setComments([]);
      setCommentsError(error instanceof Error ? error.message : 'تعذر تحميل التعليقات');
    }
  };
  useEffect(() => { void load(); }, [episodeId, orderBy]);
  useEffect(() => { void animeApi.commentRules().then(setRules).catch(() => undefined); }, []);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!user) return setMessage('سجّل الدخول أولاً لإضافة تعليق.');
    if (!text.trim()) return;
    try {
      requireMutationSuccess(await animeApi.addComment(animeLevel
        ? { epID: animeId, userID: user.userid, uniqID: user.uniqid, content: text.trim(), animeName }
        : { epID: episodeId, userID: user.userid, uniqID: user.uniqid, content: text.trim(), epName: episodeName, animeName }));
      setText(''); setMessage('تم إرسال تعليقك.'); await load();
    } catch (e) { setMessage(e instanceof Error ? e.message : 'تعذر إرسال التعليق'); }
  };
  return <section className="comments"><div className="section-heading"><div><span>المجتمع</span><h2>{animeLevel ? 'تعليقات الأنمي' : 'التعليقات'} <small>{comments.length}</small></h2></div><label className="comment-order">الترتيب<select value={orderBy} onChange={(event) => setOrderBy(event.target.value)}><option value="3">الأحدث</option><option value="1">الأكثر إعجاباً</option><option value="2">الأقدم</option></select></label></div>
    {rules && <details className="comment-rules"><summary><BookOpen /> {rules.title}</summary><p>{rules.message}</p></details>}
    <form className="comment-form" onSubmit={submit}><div className="comment-avatar">{user?.username?.[0] || <UserRound />}</div><textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={user ? 'شارك رأيك في الحلقة…' : 'سجّل الدخول للمشاركة…'} maxLength={1000} /><button aria-label="إرسال"><Send /></button></form>{message && <p className="form-message">{message}</p>}
    {commentsError && <p className="form-message error">{commentsError} <button className="link-button" onClick={() => void load()}>إعادة المحاولة</button></p>}
    <div className="comment-list">{comments.length ? comments.map((comment, index) => <CommentItem key={comment.commentID || comment.id || index} comment={comment} user={user} episodeName={episodeName} animeName={animeName} animeLevel={animeLevel} onChanged={() => load()} />) : <EmptyState icon={<MessageCircle />} title="لا توجد تعليقات بعد" body={animeLevel ? 'كن أول من يشارك رأيه في الأنمي.' : 'كن أول من يشارك رأيه في الحلقة.'} />}</div>
    {hasMore && <button className="load-more" onClick={() => load(comments.length, true)}>المزيد من التعليقات</button>}
  </section>;
}

function CommentItem({ comment, user, episodeName, animeName, animeLevel, onChanged }: { comment: Comment; user: User | null; episodeName: string; animeName: string; animeLevel: boolean; onChanged: () => void }) {
  const id = comment.commentID || comment.id || '';
  const [notice, setNotice] = useState('');
  const isOwner = Boolean(user && String(comment.userID) === String(user.userid));
  const react = async (command: 'addlike' | 'addDisLike') => {
    if (!user) return setNotice('يتطلب تسجيل الدخول');
    try { requireMutationSuccess(await animeApi.reactToComment(command, id, user)); setNotice('تم'); onChanged(); } catch { setNotice('تعذر التفاعل'); }
  };
  const edit = async () => {
    if (!user) return;
    const content = prompt('تعديل التعليق', comment.content || '');
    if (!content?.trim()) return;
    try { requireMutationSuccess(await animeApi.updateComment(id, content.trim(), episodeName, animeName, user)); setNotice('تم التعديل'); onChanged(); }
    catch { setNotice('تعذر التعديل'); }
  };
  const remove = async () => {
    if (!user || !confirm('حذف التعليق نهائياً؟')) return;
    try { requireMutationSuccess(await animeApi.deleteComment(id, user)); setNotice('تم الحذف'); onChanged(); }
    catch { setNotice('تعذر الحذف'); }
  };
  const report = async () => {
    if (!user) return setNotice('يتطلب تسجيل الدخول');
    const reported = readStoredIds('anime-cloud:reported-comments');
    if (reported.includes(id)) return setNotice('سبق أن أبلغت عن هذا التعليق');
    if (!confirm('إبلاغ الإدارة عن هذا التعليق؟')) return;
    try {
      requireMutationSuccess(await animeApi.reportComment(id, user));
      localStorage.setItem('anime-cloud:reported-comments', JSON.stringify([id, ...reported]));
      setNotice('تم إرسال البلاغ');
    }
    catch { setNotice('تعذر إرسال البلاغ'); }
  };
  return <article className="comment-item"><Image src={comment.profilePicture || comment.userImage || comment.image} alt={comment.username || 'مستخدم'} /><div className="comment-body"><div><strong>{comment.username || 'مستخدم Anime Cloud'} {comment.isAdmin === '1' && <span className="admin-badge"><Check /> إدارة</span>}</strong><time>{comment.time || ''}</time></div><p>{comment.content || ''}</p><div className="comment-actions"><button onClick={() => react('addlike')}><ThumbsUp /> {comment.likes || 0}</button><button onClick={() => react('addDisLike')}><ThumbsDown /> {comment.dislikes || 0}</button>{!animeLevel && (isOwner ? <><button onClick={edit}><Pencil /> تعديل</button><button onClick={remove}><Trash2 /> حذف</button></> : <button onClick={report}><Flag /> إبلاغ</button>)}{notice && <span>{notice}</span>}</div></div></article>;
}

function NewsPage() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useDocumentTitle('الأخبار');
  const load = () => { setLoading(true); setError(''); animeApi.news().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false)); };
  useEffect(load, []);
  return <section><div className="page-heading"><div><span>من Anime Cloud</span><h1>الأخبار</h1><p>آخر الإعلانات والتحديثات</p></div><Newspaper /></div>{loading ? <Spinner /> : error ? <ErrorState message={error} retry={load} /> : <div className="news-grid">{items.map((item) => <Link to={`/news/${item.id}`} state={item} className="news-card" key={item.id}><Image src={item.image} alt={item.title} /><div><span>خبر • {Number(item.count || 0).toLocaleString('ar')} مشاهدة</span><h2>{item.title}</h2><p>اقرأ التفاصيل <ArrowLeft /></p></div></Link>)}</div>}</section>;
}

function NewsDetailsPage() {
  const { newsId = '' } = useParams();
  const locationItem = useLocation().state as NewsItem | null;
  const [item, setItem] = useState<NewsItem | null>(locationItem);
  const [content, setContent] = useState(locationItem?.content || '');
  const [error, setError] = useState('');
  const refreshUrl = useMemo(() => extractRefreshUrl(content), [content]);
  useDocumentTitle(item?.title || 'تفاصيل الخبر');
  useEffect(() => {
    if (!item) void animeApi.news().then((items) => setItem(items.find((entry) => entry.id === newsId) || null)).catch(() => undefined);
    if (!content) void animeApi.newsDetails(newsId).then(setContent).catch((e) => setError(e.message));
  }, [newsId]);
  useEffect(() => trackOnce(`anime-cloud:visit:news:${newsId}`, () => animeApi.trackNewsVisit(newsId)), [newsId]);
  return <article className="article-page"><Link className="back-link" to="/news"><ArrowLeft /> الأخبار</Link><span>Anime Cloud News</span><h1>{item?.title || 'تفاصيل الخبر'}</h1>{item?.image && <Image src={item.image} alt={item.title} />}{error ? <ErrorState message={error} /> : <>{refreshUrl && <a className="button primary article-link" href={refreshUrl} target="_blank" rel="noreferrer">فتح الإعلان <ArrowLeft /></a>}<iframe title="محتوى الخبر" sandbox={refreshUrl ? 'allow-same-origin allow-popups allow-popups-to-escape-sandbox' : 'allow-popups allow-popups-to-escape-sandbox'} {...(refreshUrl ? { src: refreshUrl } : { srcDoc: content })} /></>}</article>;
}

function SchedulePage() {
  const [items, setItems] = useState<Anime[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useDocumentTitle('جدول العرض');
  useEffect(() => { animeApi.schedule().then(setItems).catch((e) => setError(e.message)).finally(() => setLoading(false)); }, []);
  const groups = useMemo(() => Object.entries(dayNames).map(([day, name]) => ({ day, name, items: items.filter((x) => x.day === day) })), [items]);
  return <section><div className="page-heading"><div><span>أسبوعياً</span><h1>جدول العرض</h1><p>مواعيد الأنمي المستمر حسب اليوم</p></div><CalendarDays /></div>{loading ? <Spinner /> : error ? <ErrorState message={error} /> : <div className="schedule">{groups.map((group) => <section key={group.day}><header><span>{group.day}</span><div><h2>{group.name}</h2><p>{group.items.length} أعمال</p></div></header><div>{group.items.length ? group.items.map((anime) => <AnimeCard anime={anime} wide key={anime.id} />) : <p className="schedule-empty">لا توجد أعمال مجدولة</p>}</div></section>)}</div>}</section>;
}

function LibraryPage() {
  const favorites = useFavorites();
  const watched = useWatchedAnime();
  const later = useWatchLater();
  const watching = useWatching();
  const history = useHistory();
  const [tab, setTab] = useState<'favorites' | 'watched' | 'later' | 'watching' | 'history'>('favorites');
  const [all, setAll] = useState<Anime[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useDocumentTitle('مكتبتي');
  useEffect(() => { animeApi.catalog().then(setAll).catch((e) => setError(e.message)).finally(() => setLoading(false)); }, []);
  const tabs = [
    { id: 'favorites' as const, label: 'المفضلة', icon: <Heart />, store: favorites },
    { id: 'watched' as const, label: 'تمت مشاهدته', icon: <Check />, store: watched },
    { id: 'later' as const, label: 'لاحقاً', icon: <Clock3 />, store: later },
    { id: 'watching' as const, label: 'أشاهده حالياً', icon: <Play />, store: watching },
    { id: 'history' as const, label: 'السجل', icon: <Library />, store: history }
  ];
  const active = tabs.find((item) => item.id === tab) || tabs[0];
  const ids = active.store.ids;
  const items = ids.map((id) => all.find((x) => x.id === id)).filter(Boolean) as Anime[];
  return <section><div className="page-heading"><div><span>محفوظ محلياً</span><h1>مكتبتي</h1><p>قوائم Anime Cloud وسجل المشاهدة</p></div><Library /></div><div className="tabs library-tabs">{tabs.map((item) => <button className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)} key={item.id}>{item.icon} {item.label} <span>{item.store.ids.length}</span></button>)}</div>{loading ? <Spinner /> : error ? <ErrorState message={error} /> : items.length ? <><div className="catalog-grid">{items.map((anime) => <AnimeCard anime={anime} key={anime.id} />)}</div><button className="text-danger" onClick={active.store.clear}>مسح {active.label}</button></> : <EmptyState icon={active.icon} title={`${active.label} فارغة`} body="افتح أي عمل وابدأ بناء مكتبتك." />}</section>;
}

function AuthPage() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activation, setActivation] = useState<{ email: string; password: string } | null>(null);
  const navigate = useNavigate();
  useDocumentTitle('تسجيل الدخول');
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault(); setLoading(true); setMessage('');
    const values = Object.fromEntries(new FormData(event.currentTarget));
    try {
      const email = String(values.email);
      const password = String(values.password);
      if (mode === 'signup') {
        const data = await animeApi.signup(String(values.username), email, password);
        const status = data.result?.[0]?.status;
        if (status === 'EmailExsist') throw new Error('البريد الإلكتروني مستخدم بالفعل.');
        if (status === 'UsernameExsist') throw new Error('اسم المستخدم مستخدم بالفعل.');
        if (status !== 'Done') throw new Error('تعذر إنشاء الحساب. راجع البيانات وحاول مجدداً.');
        setActivation({ email, password });
        setMessage('تم إنشاء الحساب. أدخل رمز التفعيل المرسل إلى بريدك.');
        return;
      }
      const data = await animeApi.login(email, password);
      const user = data.result?.[0];
      const status = data.result?.[1]?.status || user?.status;
      if (!user || status !== 'true' || !user.userid) throw new Error('البريد الإلكتروني أو كلمة المرور غير صحيحة.');
      if (user.confirm && !['true', '1'].includes(user.confirm)) {
        setActivation({ email, password });
        setMessage('الحساب يحتاج إلى تفعيل. أدخل الرمز المرسل إلى بريدك.');
        return;
      }
      saveUser(user); navigate('/');
    } catch (e) { setMessage(e instanceof Error ? e.message : 'حدث خطأ'); }
    finally { setLoading(false); }
  };
  const activate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!activation) return;
    setLoading(true); setMessage('');
    try {
      const code = String(new FormData(event.currentTarget).get('code') || '');
      const result = await animeApi.activate(code, activation.email, activation.password);
      if (result.result?.[0]?.status !== 'Done') throw new Error('رمز التفعيل غير صحيح.');
      const login = await animeApi.login(activation.email, activation.password);
      const user = login.result?.[0];
      const status = login.result?.[1]?.status || user?.status;
      if (!user || status !== 'true' || !user.userid) throw new Error('تم التفعيل. سجّل الدخول من جديد.');
      saveUser(user); navigate('/');
    } catch (e) { setMessage(e instanceof Error ? e.message : 'تعذر التفعيل'); }
    finally { setLoading(false); }
  };
  const recover = async () => {
    const email = prompt('أدخل بريدك الإلكتروني');
    if (!email) return;
    try {
      const result = await animeApi.recover(email);
      const status = result.result?.[0]?.status;
      setMessage(status === 'Done' ? 'تم إرسال تعليمات الاستعادة إلى بريدك.' : 'البريد الإلكتروني غير موجود.');
    } catch (e) { setMessage(e instanceof Error ? e.message : 'تعذر إرسال طلب الاستعادة'); }
  };
  return <section className="auth-page"><div className="auth-art"><img src="/assets/app-icon.png" alt="Anime Cloud" /><span>AC / ACCOUNT</span><h1>مكتبتك.<br />في كل مكان.</h1><p>سجّل الدخول للتعليقات ومزامنة إعدادات حسابك.</p></div><div className="auth-panel">{activation ? <><div className="auth-code"><span>تفعيل الحساب</span><h2>تحقق من بريدك</h2><p>أرسلنا رمز التفعيل إلى {activation.email}</p></div><form onSubmit={activate}><label>رمز التفعيل<input required name="code" inputMode="numeric" autoComplete="one-time-code" /></label><button className="button primary wide-button" disabled={loading}>{loading ? 'جاري التحقق…' : 'تفعيل الحساب'} <Check /></button>{message && <p className={`form-message ${message.includes('غير') || message.includes('تعذر') ? 'error' : ''}`}>{message}</p>}</form><button className="link-button" onClick={() => setActivation(null)}>العودة</button></> : <><div className="tabs"><button className={mode === 'login' ? 'active' : ''} onClick={() => { setMode('login'); setMessage(''); }}>تسجيل الدخول</button><button className={mode === 'signup' ? 'active' : ''} onClick={() => { setMode('signup'); setMessage(''); }}>حساب جديد</button></div><form onSubmit={submit}>{mode === 'signup' && <label>اسم المستخدم<input required name="username" autoComplete="username" /></label>}<label>البريد الإلكتروني<input required type="email" name="email" autoComplete="email" /></label><label>كلمة المرور<input required minLength={6} type="password" name="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} /></label><button className="button primary wide-button" disabled={loading}>{loading ? 'جاري الاتصال…' : mode === 'login' ? 'دخول' : 'إنشاء الحساب'} <ArrowLeft /></button>{message && <p className="form-message error">{message}</p>}</form>{mode === 'login' && <button className="link-button" onClick={recover}>نسيت كلمة المرور؟</button>}</>}</div></section>;
}

function SettingsPage() {
  const user = useUser();
  const [theme, setTheme] = useState(() => localStorage.getItem('anime-cloud:theme') || 'dark');
  const [comments, setComments] = useState(() => localStorage.getItem('anime-cloud:comments') !== 'false');
  const [seen, setSeen] = useState(() => localStorage.getItem('anime-cloud:seen') !== 'false');
  const [childrenMode, setChildrenMode] = useState(() => localStorage.getItem('anime-cloud:children-mode') === 'true');
  const [hiddenMode, setHiddenMode] = useState(() => localStorage.getItem('anime-cloud:hidden-mode') === 'true');
  const [accountMessage, setAccountMessage] = useState('');
  const [subscription, setSubscription] = useState('');
  const [profilePreview, setProfilePreview] = useState('');
  useDocumentTitle('الإعدادات');
  useEffect(() => { document.documentElement.dataset.theme = theme; localStorage.setItem('anime-cloud:theme', theme); }, [theme]);
  useEffect(() => localStorage.setItem('anime-cloud:comments', String(comments)), [comments]);
  useEffect(() => localStorage.setItem('anime-cloud:seen', String(seen)), [seen]);
  useEffect(() => localStorage.setItem('anime-cloud:hidden-mode', String(hiddenMode)), [hiddenMode]);
  useEffect(() => {
    if (!user) return;
    void animeApi.subscribeDays(user).then((response) => {
      const item = response.result?.[0];
      setSubscription(item?.days || item?.subscribe || '');
      if (item && (item.subscribe !== user.subscribe || item.lastBackupTime !== user.lastBackupTime)) {
        saveUser({ ...user, subscribe: item.subscribe ?? user.subscribe, lastBackupTime: item.lastBackupTime ?? user.lastBackupTime });
      }
    }).catch(() => undefined);
  }, [user?.userid]);
  const logout = () => {
    void animeApi.logout().catch(() => undefined);
    saveUser(null);
  };
  const pickImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;
    if (!file.type.startsWith('image/')) { setAccountMessage('اختر ملف صورة صالحاً.'); return; }
    if (file.size > 8_000_000) { setAccountMessage('الصورة أكبر من 8MB.'); return; }
    setAccountMessage('جاري رفع الصورة…');
    let preview = '';
    try {
      const jpeg = await profileJpeg(file);
      preview = URL.createObjectURL(jpeg);
      setProfilePreview(preview);
      const response = await animeApi.uploadProfile(jpeg);
      const result = Array.isArray(response.result) ? response.result[0] : response.result;
      const imageUrl = result?.imageUrl || response.imageUrl;
      if (!imageUrl) throw new Error('الخادم لم يؤكد رفع الصورة');
      saveUser({ ...user, profilePicture: imageUrl });
      setAccountMessage('تم تحديث الصورة.');
    } catch (error) { setAccountMessage(error instanceof Error ? error.message : 'تعذر رفع الصورة'); }
    finally { if (preview) URL.revokeObjectURL(preview); setProfilePreview(''); event.target.value = ''; }
  };
  const changeUsername = async () => {
    if (!user) return;
    const newUsername = prompt('اسم المستخدم الجديد', user.username)?.trim();
    if (!newUsername || newUsername === user.username) return;
    try {
      const result = await animeApi.changeUsername(newUsername, user);
      requireMutationSuccess(result);
      const row = result.result?.[0];
      saveUser({ ...user, username: row?.username || newUsername }); setAccountMessage('تم تغيير اسم المستخدم.');
    } catch (error) { setAccountMessage(error instanceof Error ? error.message : 'تعذر تغيير اسم المستخدم.'); }
  };
  const changePassword = async () => {
    if (!user) return;
    const oldPassword = prompt('كلمة المرور الحالية');
    const newPassword = prompt('كلمة المرور الجديدة (6 أحرف على الأقل)');
    if (!oldPassword || !newPassword || newPassword.length < 6) return;
    try {
      const result = await animeApi.changePassword(oldPassword, newPassword, user);
      requireMutationSuccess(result);
      setAccountMessage('تم تغيير كلمة المرور.');
    } catch (error) { setAccountMessage(error instanceof Error ? error.message : 'تعذر تغيير كلمة المرور.'); }
  };
  const uploadNativeBackup = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user) return;
    try {
      if (file.size > 20_000_000) throw new Error('ملف قاعدة البيانات أكبر من 20MB.');
      const signature = new TextDecoder().decode(await file.slice(0, 16).arrayBuffer());
      if (signature !== 'SQLite format 3\0') throw new Error('الملف ليس قاعدة Anime Cloud SQLite صالحة.');
      setAccountMessage('جاري رفع نسخة iOS السحابية…');
      requireMutationSuccess(await animeApi.uploadNativeBackup(file));
      setAccountMessage('تم حفظ نسخة iOS السحابية.');
    } catch (error) { setAccountMessage(error instanceof Error ? error.message : 'تعذر رفع النسخة السحابية'); }
    finally { event.target.value = ''; }
  };
  const importNativeBackup = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setAccountMessage('جاري قراءة نسخة iPhone…');
      applyNativeBackupState(await animeApi.importNativeBackup(file));
      window.location.reload();
    } catch (error) { setAccountMessage(error instanceof Error ? error.message : 'تعذر استيراد نسخة iPhone'); }
    finally { event.target.value = ''; }
  };
  const restoreNativeCloudBackup = async () => {
    try {
      setAccountMessage('جاري استعادة نسخة iOS السحابية…');
      applyNativeBackupState(await animeApi.restoreNativeCloudBackup());
      window.location.reload();
    } catch (error) { setAccountMessage(error instanceof Error ? error.message : 'تعذر استعادة النسخة السحابية'); }
  };
  const pinHash = async (pin: string) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(pin)))).map((byte) => byte.toString(16).padStart(2, '0')).join('');
  const toggleChildrenMode = async (enabled: boolean) => {
    const storedHash = localStorage.getItem('anime-cloud:children-pin');
    if (enabled) {
      const pin = prompt('اختر رمزاً من 4 أرقام لوضع الأطفال');
      if (!pin || !/^\d{4}$/.test(pin)) { setAccountMessage('رمز الأطفال يجب أن يكون 4 أرقام.'); return; }
      localStorage.setItem('anime-cloud:children-pin', await pinHash(pin));
    } else if (storedHash) {
      const pin = prompt('أدخل رمز وضع الأطفال');
      if (!pin || await pinHash(pin) !== storedHash) { setAccountMessage('رمز وضع الأطفال غير صحيح.'); return; }
    }
    setChildrenMode(enabled);
    localStorage.setItem('anime-cloud:children-mode', String(enabled));
    setAccountMessage('سيُطبّق الفلتر عند فتح الصفحة التالية.');
  };
  const exportBackup = () => {
    const data = Object.fromEntries(SAFE_BACKUP_KEYS.map((key) => [key, localStorage.getItem(key)]));
    const backup = { format: 'anime-cloud-web-backup', version: 1, createdAt: new Date().toISOString(), data };
    const url = URL.createObjectURL(new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a'); link.href = url; link.download = 'anime-cloud-backup.json'; link.click(); URL.revokeObjectURL(url);
  };
  const importBackup = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    try {
      const parsed = JSON.parse(await file.text()) as Record<string, unknown>;
      const data = parsed?.format === 'anime-cloud-web-backup' && parsed.version === 1 ? parsed.data : parsed;
      if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('بنية النسخة غير صالحة');
      const source = data as Record<string, unknown>;
      const updates = SAFE_BACKUP_KEYS.flatMap((key) => source[key] == null ? [] : [[key, validateBackupValue(key, source[key])] as const]);
      updates.forEach(([key, value]) => localStorage.setItem(key, value));
      window.location.reload();
    }
    catch (error) { alert(error instanceof Error ? `ملف النسخة الاحتياطية غير صالح: ${error.message}` : 'ملف النسخة الاحتياطية غير صالح.'); }
  };
  return <section>
    <div className="page-heading"><div><span>Anime Cloud Web</span><h1>الإعدادات</h1><p>الحساب وتجربة المشاهدة</p></div><Settings /></div>
    <div className="settings-layout">
      <section className="profile-card"><label className="profile-avatar editable">{profilePreview || user?.profilePicture ? <Image src={profilePreview || user?.profilePicture} alt={user?.username || 'الحساب'} /> : (user?.username?.[0] || <UserRound />)}{user && <input type="file" accept="image/*" onChange={pickImage} />}</label><div><h2>{user?.username || 'زائر'}</h2><p>{user?.email || 'لا يوجد حساب متصل'} {user && '• اضغط الصورة لتغييرها'} {subscription && `• الاشتراك: ${subscription}`}</p></div>{user ? <button className="button ghost" onClick={logout}>تسجيل الخروج</button> : <Link className="button primary" to="/auth"><LogIn /> تسجيل الدخول</Link>}</section>
      {accountMessage && <p className="settings-message">{accountMessage}</p>}
      {user && <section className="settings-group"><h2>الحساب</h2><SettingRow icon={<UserRound />} title="تغيير اسم المستخدم" body={user.username}><button className="button" onClick={changeUsername}>تغيير</button></SettingRow><SettingRow icon={<Settings />} title="تغيير كلمة المرور" body="تحديث كلمة مرور حساب Anime Cloud"><button className="button" onClick={changePassword}>تغيير</button></SettingRow></section>}
      <section className="settings-group"><h2>المظهر</h2><SettingRow icon={theme === 'dark' ? <Moon /> : <Sun />} title="الوضع الداكن" body="مظهر مريح للمشاهدة ليلاً"><Switch checked={theme === 'dark'} onChange={(value) => setTheme(value ? 'dark' : 'light')} /></SettingRow></section>
      <section className="settings-group"><h2>المشاهدة</h2><SettingRow icon={<BookOpen />} title="تمييز الحلقات المشاهدة" body="حفظ كل حلقة تمت مشاهدتها"><Switch checked={seen} onChange={setSeen} /></SettingRow><SettingRow icon={<MessageCircle />} title="إظهار التعليقات" body="عرض مجتمع Anime Cloud أسفل المشغل"><Switch checked={comments} onChange={setComments} /></SettingRow><SettingRow icon={<UserRound />} title="وضع الأطفال" body="تصفية المحتوى باستخدام رمز محلي"><Switch checked={childrenMode} onChange={(value) => void toggleChildrenMode(value)} /></SettingRow><SettingRow icon={<X />} title="إخفاء المحتوى الحساس" body="إرسال hiddenMode إلى مكتبة Anime Cloud"><Switch checked={hiddenMode} onChange={setHiddenMode} /></SettingRow></section>
      <section className="settings-group"><h2>النسخة الاحتياطية</h2><SettingRow icon={<Download />} title="تصدير بياناتي" body="حفظ المفضلة والحلقات المشاهدة والإعدادات"><button className="button" onClick={exportBackup}>تصدير</button></SettingRow><SettingRow icon={<Library />} title="استعادة بياناتي" body="استيراد نسخة Anime Cloud Web"><label className="button import-button">استيراد<input type="file" accept="application/json" onChange={importBackup} /></label></SettingRow><SettingRow icon={<Library />} title="استيراد animeDB.sqlite" body="ينقل المفضلة والمشاهدة والسجل والبلاغات من تطبيق iPhone"><label className="button import-button">استيراد<input type="file" accept=".sqlite,.db,application/octet-stream" onChange={importNativeBackup} /></label></SettingRow></section>
      {user && <section className="settings-group"><h2>نسخة iOS السحابية</h2><SettingRow icon={<Library />} title="رفع animeDB.sqlite" body="نفس saveBackup وحقول تطبيق iPhone"><label className="button import-button">رفع<input type="file" accept=".sqlite,.db,application/octet-stream" onChange={uploadNativeBackup} /></label></SettingRow><SettingRow icon={<Download />} title="استعادة النسخة السحابية" body="ينقل بيانات ملف Anime Cloud الأصلي إلى الويب"><button className="button" onClick={() => void restoreNativeCloudBackup()}>استعادة</button></SettingRow><SettingRow icon={<Download />} title="تنزيل ملف iOS" body="حفظ animeDB.sqlite الأصلي"><a className="button" href="/api/account-backup" download="animeDB.sqlite">تنزيل</a></SettingRow></section>}
      <section className="settings-group"><h2>عن التطبيق</h2><SettingRow icon={<Film />} title="Anime Cloud Web" body="إعادة بناء لعميل iOS 6.5"><span className="version">1.0.0</span></SettingRow></section>
    </div>
  </section>;
}

function SettingRow({ icon, title, body, children }: { icon: ReactNode; title: string; body: string; children: ReactNode }) {
  return <div className="setting-row"><span className="setting-icon">{icon}</span><div><strong>{title}</strong><p>{body}</p></div>{children}</div>;
}

function Switch({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return <button className={`switch ${checked ? 'on' : ''}`} role="switch" aria-checked={checked} onClick={() => onChange(!checked)}><span /></button>;
}

function NotFound() {
  useDocumentTitle('غير موجود');
  return <EmptyState icon={<X />} title="الصفحة غير موجودة" body="الرابط قديم أو غير صحيح." />;
}

function AuthRoute() {
  const user = useUser();
  return user ? <Navigate to="/settings" replace /> : <AuthPage />;
}

export default function App() {
  useEffect(() => { document.documentElement.dataset.theme = localStorage.getItem('anime-cloud:theme') || 'dark'; }, []);
  return <AppShell><Routes>
    <Route path="/" element={<HomePage />} />
    <Route path="/catalog" element={<CatalogPage />} />
    <Route path="/anime/:animeId" element={<AnimeDetailsPage />} />
    <Route path="/watch/:episodeId" element={<WatchPage />} />
    <Route path="/news" element={<NewsPage />} />
    <Route path="/news/:newsId" element={<NewsDetailsPage />} />
    <Route path="/schedule" element={<SchedulePage />} />
    <Route path="/library" element={<LibraryPage />} />
    <Route path="/auth" element={<AuthRoute />} />
    <Route path="/settings" element={<SettingsPage />} />
    <Route path="*" element={<NotFound />} />
  </Routes></AppShell>;
}
